// TODO: refactor, this is a patch to keep compatibility with v1
import * as React from 'react';
import styled from 'styled-components';
import Link, { LinkProps } from 'next/link';

import { CallToAction as CallToActionType, Page, TLD, MAIN_CATEGORIES } from '@packages/common';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { Button, themeGet } from '@packages/ui';

const APP_TLD = getPublicRuntimeConfig('APP_TLD');

const urls = [
  'https://www.moebel.de',
  'https://www.meubles.fr',
  'https://www.meubelo.nl',
  'https://moebel.de',
  'https://meubles.fr',
  'https://meubelo.nl',
  'www.moebel.de',
  'www.meubles.fr',
  'www.meubelo.nl',
  'https://end-user-frontend.staging.moebel.de',
  'https://end-user-frontend.staging.meubles.fr',
  'https://end-user-frontend.staging.meubelo.nl',
];

export function isInternal(url: string): boolean {
  if (!url.includes('service')) {
    for (const item of urls) {
      if (url.indexOf(item) > -1) {
        return true;
      }
    }
  }
  return false;
}

export function isKitchenUrl(url: string): boolean {
  // Add more kitchen links here if needed, like /kuechenberater etc.
  return ['moebel.de/kuechen'].some((u) => url.includes(u));
}

export function getLinkData(url: string, tld: TLD): { href: LinkProps['href']; as: string } {
  let href = url;
  for (const item of urls) {
    if (href.indexOf(item) > -1 && !isKitchenUrl(url)) {
      href = href.replace(item, '');
    }
  }
  for (const item of MAIN_CATEGORIES[tld]) {
    if (
      url.indexOf(`meubles.fr${item}`) > -1 ||
      url.indexOf(`moebel.de${item}`) > -1 ||
      url.indexOf(`meubelo.nl${item}`) > -1
    ) {
      return {
        href: { href: Page.category, query: { categoryPath: href } },
        as: href,
      };
    }
  }
  return {
    href: Page.contentful,
    as: href,
  };
}

export const A = styled.a`
  color: ${themeGet.colors('anchorLinkColor')};
  font-size: ${themeGet.fontSize(4)};
  font-weight: ${themeGet.fontWeight('lighter')};
  &:hover {
    color: ${themeGet.colors('primary')};
  }
`;
export const Fallback = styled.div.attrs({ 'data-testid': 'call-to-action-fallback' })`
  padding: 0;
  height: auto;
`;

export type CallToActionProps = CallToActionType & {
  onClick?: (item: CallToActionProps) => void;
  className?: string;
  as?: React.ElementType; // TODO: check if this is actually required - it's not used here, but other components pass this property (like <Teaser />)
  fallbackAs?: React.ElementType;
};

export const CallToAction: React.FC<CallToActionProps> = (props) => {
  const { url, title, target, children, rel, onClick, className, fallbackAs, as: renderAs } = props;
  const relJoined = rel && rel.join(' ');

  const handleClick = () => {
    if (onClick) {
      onClick(props);
    }
  };

  if (!url) {
    return (
      <Fallback as={fallbackAs} className={className}>
        {children}
      </Fallback>
    );
  }

  const { as } = getLinkData(url, APP_TLD);

  const Wrapper = typeof children === 'string' ? Button : A;
  // TODO re-write whole CallToAction and components using it.
  // renderAs is not used properly anymore and does not work same way it was working in V1.
  // For now, any change in this place breaks a lot of contentful modules
  if (isKitchenUrl(url)) {
    const ctaChildren = (
      <Wrapper
        href={url}
        variant={(renderAs as any)?.displayName === 'TagLink' ? 'tag' : 'primary'}
        target={target}
        rel={relJoined}
        title={title}
        onClick={handleClick}
        className={className}
      >
        {children}
      </Wrapper>
    );
    // Do not use next/link here, as it will implicitly convert moebel.de/kuechen
    // to just /kuechen if current domain is moebel.de
    return typeof children === 'string' ? (
      <a href={url} data-testid="kitchen-link">
        {ctaChildren}
      </a>
    ) : (
      ctaChildren
    );
  }

  if (isInternal(url)) {
    return (
      <Link href={as} prefetch={false}>
        <Wrapper
          href={as as string}
          variant={(renderAs as any)?.displayName === 'TagLink' ? 'tag' : 'primary'}
          target={target}
          rel={relJoined}
          title={title}
          onClick={handleClick}
          className={className}
        >
          {children}
        </Wrapper>
      </Link>
    );
  }

  // external link
  return (
    <Link href={as} prefetch={false}>
      <Wrapper
        href={as}
        variant={(renderAs as any)?.displayName === 'TagLink' ? 'tag' : 'primary'}
        target={target}
        rel={relJoined}
        title={title}
        onClick={handleClick}
        className={className}
        as="a"
      >
        {children}
      </Wrapper>
    </Link>
  );
};
